import React, { useState, useEffect } from 'react';
import { Button, Checkbox, Input, Modal, message, Space } from 'antd';
import { tid } from '@utils/test';
import useUpdateDashboardMeta from '../useUpdateDashboardMeta';
import useCloneDashboardMeta from '../useCloneDashboardMeta';
import { FlexFill, FooterWrapper, Title } from '../utils/styled';
import { useSelector, useDispatch } from 'react-redux';
import { publicSavedDashboardsSelector } from '@store/common/selectors';
import * as tempDashboardActions from '@store/tempDashboard/actions';
import { getList } from '@store/dashboards/list/actions';
import { ACTIONTYPES } from '../utils';

function SaveDashboardMetaPopup({
  id,
  dashboard,
  isPopupOpen,
  closePopup,
  dataToBeSaved,
  onSaveCallback,
  type,
}) {
  const { name, isSaved, isPublic } = dataToBeSaved;
  const [publicChecked, setPublicChecked] = useState(isPublic);
  const isCloneClick = type && type === ACTIONTYPES.CLONE;
  const [dashboardName, setDashboardName] = useState(name);
  const { updateMetaMutation } = useUpdateDashboardMeta();
  const { cloneMutation } = useCloneDashboardMeta();
  const publicSavedDashboardsEnabled = useSelector(publicSavedDashboardsSelector);
  const dispatch = useDispatch();
  useEffect(() => {
    if (isPopupOpen && isCloneClick) setDashboardName(`Clone ${name}`);
  }, [isPopupOpen, setDashboardName, isCloneClick, name]);

  const onNameChange = (event) => {
    setDashboardName(event.target.value);
  };

  const togglePublic = (event) => {
    setPublicChecked(event.target.checked);
  };

  const payload = {
    name: dashboardName,
    isSaved,
    isPublic: publicChecked,
  };

  const callUpdateHandler = async () => {
    try {
      const { data } = await updateMetaMutation.mutateAsync({
        id,
        payload,
      });
      closePopup();
      // update dashboard title in tabs
      dispatch(tempDashboardActions.updatedSavedDashboard(id, dashboardName));
      // refetch the list within saved dashboard tab
      dispatch(getList());
      onSaveCallback();

      message.success('Dashboard updated successfully');
    } catch (error: any) {
      if (error.response && error.response.data) {
        message.error(error.response.data.message);
      }
    }
  };

  const callCloneHandler = async () => {
    try {
      const { data } = await cloneMutation.mutateAsync({
        id,
        payload: { id, name: dashboardName, isPublic: publicChecked },
      });
      closePopup();

      dispatch(getList(data.id));
      onSaveCallback();
      message.success('Dashboard cloned successfully');
    } catch (error: any) {
      if (error.response && error.response.data) {
        message.error(error.response.data.message);
      }
    }
  };

  const modalTitle = <Title>{isCloneClick ? `Clone Dashboard` : `Update Dashboard`}</Title>;
  const makeItPublicEnableCondition = !isSaved || isCloneClick || !isPublic;

  const footerButtons = [
    <FooterWrapper
      key="sd-list-update-popup-footer-wrapper"
      {...tid('sd-list-update-popup-footer-wrapper')}
    >
      {publicSavedDashboardsEnabled || dashboard?.productName === 'app' ? (
        <Checkbox
          checked={publicChecked}
          onChange={togglePublic}
          disabled={!makeItPublicEnableCondition}
        >
          Make it Public
        </Checkbox>
      ) : null}
      <FlexFill />
      <Space>
        <Button key="sd-list-update-popup-footer-cancel" onClick={closePopup}>
          Cancel
        </Button>
        <Button
          type="primary"
          key="sd-popup-save"
          disabled={dashboardName ? false : true}
          onClick={isCloneClick ? callCloneHandler : callUpdateHandler}
        >
          {isCloneClick ? 'Clone' : 'Update'}
        </Button>
      </Space>
    </FooterWrapper>,
  ];

  return (
    <Modal
      title={modalTitle}
      width={400}
      open={isPopupOpen}
      okText="Update"
      cancelText="Cancel"
      footer={footerButtons}
      maskClosable={true}
      onCancel={closePopup}
    >
      <Input value={dashboardName} placeholder="Dashboard Name" onChange={onNameChange} />
    </Modal>
  );
}

export default SaveDashboardMetaPopup;
